// @ts-nocheck
/* remove this rule @ts-nocheck, if you are working in this file, it was added to allow tests to run.
 but for proper development, type checks are necessary.
 also be aware that you will need to add the rule again if you commit changes to the file. */
/* eslint-disable no-bitwise */
import * as sjcl from 'sjcl';
import { ArrayBufferBuilder } from './buffer.builder';
import { ArrayBufferDestructor } from './buffer.destructor';
import { ConverterService } from '../converter/converter.service';
import { Injectable } from '@angular/core';
import { SJCLBitArray, fromArrayBufferToBits, fromBitsToArrayBuffer } from './sjcl-bit-array.type';

@Injectable()
export class CryptoProvider {
  private readonly crypto = window.crypto;

  private static readonly ALGORITHM = 'AES-CBC'; // bytes
  private static readonly IV_SIZE = 16; // bytes
  private static readonly SALT_SIZE = 16; // bytes
  private static readonly HASH_LENGTH_16_BYTE = 16 * 8;
  private static readonly HASH_LENGTH_32_BYTE = 32 * 8;

  constructor(private converterService: ConverterService) {}

  public async decrypt(
    passphrase: string,
    saltIvCipher: Blob | string,
    uint: Uint8ArrayConstructor,
  ): Promise<Uint8Array>;

  public async decrypt(
    passphrase: string,
    saltIvCipher: Blob | string,
    uint?: Uint16ArrayConstructor,
  ): Promise<Uint16Array>;

  public async decrypt(
    passphrase: string,
    saltIvCipher: Blob | string,
    uint: Uint8ArrayConstructor | Uint16ArrayConstructor = Uint16Array,
  ): Promise<Uint8Array | Uint16Array> {
    const saltStringLength = CryptoProvider.SALT_SIZE * 2;
    const ivStringLength = CryptoProvider.IV_SIZE * 2;
    const dataStringStart = saltStringLength + ivStringLength;
    const saltIvCipherHex =
      typeof saltIvCipher === 'string' ? saltIvCipher : await CryptoProvider.blobToHexString(saltIvCipher);
    const [salt, iv, data] = [
      saltIvCipherHex.substring(0, saltStringLength),
      this.converterService.hexStringToUint8Array(saltIvCipherHex.substring(saltStringLength, dataStringStart)),
      this.converterService.hexStringToUint8Array(saltIvCipherHex.substring(dataStringStart)),
    ];

    const salt2 = sjcl.codec.hex.toBits(salt) as SJCLBitArray;
    const [key] = await this.getKey(passphrase, salt2);
    const ab = await this.crypto.subtle.decrypt(
      {
        name: CryptoProvider.ALGORITHM,
        iv,
      },
      key,
      data,
    );
    return new uint(ab);
  }

  public async decryptToHexString(password: string, cryptedPin: string): Promise<string> {
    const data = await this.decrypt(password, cryptedPin);
    return this.converterService.uIntArrayToString(data);
  }

  public getRandom(stringLength = 32): string {
    const arr = new Uint8Array(stringLength / 2);
    this.crypto.getRandomValues(arr);
    return Array.from(arr, CryptoProvider.dec2hex).join('');
  }

  public async getOldAndNewHashAsArray(secret: string, salt: Uint8Array): Promise<Uint8Array[]> {
    return [await this.computeHash(secret, salt, true), await this.computeHash(secret, salt)];
  }

  //TODO remove old
  public async computeHash(value: string, salt: Uint8Array, old = false): Promise<Uint8Array> {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey('raw', enc.encode(value), 'PBKDF2', false, ['deriveBits']);
    const buffer = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt,
        iterations: old ? 1000 : 600000,
        hash: old ? 'SHA-1' : 'SHA-256',
      },
      key,
      old ? CryptoProvider.HASH_LENGTH_16_BYTE : CryptoProvider.HASH_LENGTH_32_BYTE,
    );

    return new Uint8Array(buffer);
  }

  public async encryptToBase64(passphrase: string, dataToEncrypt: Uint8Array | Uint16Array): Promise<string> {
    const encryptionResult = await this.encrypt(passphrase, dataToEncrypt);
    const combinedBuffer = new ArrayBufferBuilder()
      .add(encryptionResult.salt)
      .add(encryptionResult.iv)
      .add(encryptionResult.encryptedData)
      .build();

    return this.converterService.uIntArrayToBase64(combinedBuffer);
  }

  public async encryptToHex(passphrase: string, plaintext: Uint8Array | string | Uint16Array): Promise<string> {
    let dataToEncrypt: Uint8Array | Uint16Array;

    if (typeof plaintext === 'string') {
      dataToEncrypt = this.converterService.stringToUint16Array(plaintext);
    } else {
      dataToEncrypt = plaintext;
    }

    const encryptionResult = await this.encrypt(passphrase, dataToEncrypt);

    const ciphertext = CryptoProvider.unit8ArrayToHexString(new Uint8Array(encryptionResult.encryptedData));
    return `${sjcl.codec.hex.fromBits(encryptionResult.salt)}${CryptoProvider.unit8ArrayToHexString(encryptionResult.iv)}${ciphertext}`;
  }

  public async decryptFromBase64(passphrase: string, cipherText: string): Promise<ArrayBuffer> {
    const buffer = this.converterService.base64ToUintArray(cipherText);

    const [salt, iv, cipher] = ArrayBufferDestructor.destructArrayBuffer(buffer, [
      CryptoProvider.SALT_SIZE,
      CryptoProvider.IV_SIZE,
    ]);

    const salt2 = fromArrayBufferToBits(salt);
    const [key] = await this.getKey(passphrase, salt2);
    return await this.crypto.subtle.decrypt(
      {
        name: CryptoProvider.ALGORITHM,
        iv,
      },
      key,
      cipher,
    );
  }

  private async encrypt(
    passphrase: string,
    dataToEncrypt: Uint8Array | Uint16Array,
  ): Promise<{
    salt: SJCLBitArray;
    encryptedData: ArrayBuffer;
    iv: Uint8Array;
  }> {
    const ivBuf = this.crypto.getRandomValues(new Uint8Array(CryptoProvider.IV_SIZE));
    const [key, saltBuf] = await this.getKey(passphrase);
    const result = await this.crypto.subtle.encrypt(
      {
        name: CryptoProvider.ALGORITHM,
        iv: ivBuf,
      },
      key,
      dataToEncrypt,
    );

    return {
      salt: saltBuf,
      iv: ivBuf,
      encryptedData: result,
    };
  }

  private async getKey(passphrase: string, salt?: SJCLBitArray): Promise<[CryptoKey, SJCLBitArray]> {
    salt ??= sjcl.codec.hex.toBits(this.getRandom(CryptoProvider.SALT_SIZE * 2));

    const hmacSHA256 = function (key: sjcl.bitArray): void {
      const hasher = new sjcl.misc.hmac(key, sjcl.hash.sha256);

      this.encrypt = (...args: Array<sjcl.bitArray | string>) => hasher.encrypt(...args);
    };

    const derivedKey = sjcl.misc.pbkdf2(passphrase, salt, 12000, 128, hmacSHA256) as sjcl.bitArray;
    const key2 = await this.crypto.subtle.importKey(
      'raw',
      fromBitsToArrayBuffer(derivedKey),
      CryptoProvider.ALGORITHM,
      true,
      ['encrypt', 'decrypt'],
    );
    return [key2, salt as SJCLBitArray];
  }

  private static dec2hex(dec: number): string {
    const hex = dec.toString(16);
    return hex.length === 1 ? '0' + hex : hex.substring(hex.length - 2);
  }

  private static blobToHexString(blob: Blob): Promise<string> {
    return new Promise((resolve: (value: string) => void): void => {
      const fileReader = new FileReader();
      fileReader.onload = () => {
        const buffer = new Uint8Array(fileReader.result as ArrayBuffer);
        resolve(CryptoProvider.unit8ArrayToHexString(buffer));
      };
      fileReader.readAsArrayBuffer(blob);
    });
  }

  private static unit8ArrayToHexString(buf: Uint8Array): string {
    const hexChars = [];
    for (const bite of buf) {
      hexChars.push((bite >>> 4).toString(16));
      hexChars.push((bite & 0x0f).toString(16));
    }

    return hexChars.join('');
  }
}
